/**
 * Keeps a sticky promo strip docked directly beneath the (sticky) store header.
 * The header shrinks when its menu slides out on scroll, so its height is
 * observed and written to the '--promo-strip-top' custom property.
 */
if (!customElements.get('promo-strip-sticky')) {
  class PromoStripSticky extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section');
      this.header = document.querySelector('store-header');
      this.update = this.update.bind(this);

      this.resizeObserver = new ResizeObserver(this.update);
      if (this.header) this.resizeObserver.observe(this.header);
      if (this.section) this.resizeObserver.observe(this.section);

      window.addEventListener('on:breakpoint-change', this.update);
      this.update();
    }

    disconnectedCallback() {
      this.resizeObserver.disconnect();
      window.removeEventListener('on:breakpoint-change', this.update);
      document.documentElement.style.removeProperty('--promo-strip-top');
    }

    update() {
      if (this.frame) return;

      this.frame = requestAnimationFrame(() => {
        this.frame = null;

        // The sticky header sits at 'top: -1px', so the strip starts 1px above its height.
        const headerIsSticky = this.header && this.header.dataset.isSticky;
        const top = headerIsSticky ? this.header.offsetHeight - 1 : 0;
        document.documentElement.style.setProperty('--promo-strip-top', `${top}px`);

        // Let sticky page elements (filters, galleries, menus) clear the strip too.
        if (headerIsSticky && typeof this.header.setHeaderEnd === 'function') {
          this.header.setHeaderEnd();
        }
      });
    }
  }

  customElements.define('promo-strip-sticky', PromoStripSticky);
}

/**
 * Counts down to a date/time entered in the theme editor, in the chosen time zone.
 */
if (!customElements.get('promo-countdown')) {
  class PromoCountdown extends HTMLElement {
    connectedCallback() {
      this.endTime = this.getEndTime();
      this.units = {
        days: this.querySelector('.js-days'),
        hours: this.querySelector('.js-hours'),
        minutes: this.querySelector('.js-minutes'),
        seconds: this.querySelector('.js-seconds')
      };
      this.daysUnit = this.querySelector('.js-days-unit');

      if (!this.endTime) {
        if (!(window.Shopify && Shopify.designMode)) {
          this.block.hidden = true;
          this.endCountdownCopy();
        }
        return;
      }

      this.tick();
      this.interval = setInterval(this.tick.bind(this), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
    }

    get block() {
      return this.closest('.promo-strip__countdown') || this;
    }

    /**
     * Converts the editor's date and time into a UTC timestamp.
     * @returns {?number} Milliseconds since epoch, or null if the date is invalid.
     */
    getEndTime() {
      const date = (this.dataset.endDate || '').trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
      const time = (this.dataset.endTime || '23:59').trim().match(/^(\d{1,2}):(\d{2})$/);
      if (!date || !time) return null;

      const [year, month, day] = date.slice(1).map(Number);
      const [hour, minute] = time.slice(1).map(Number);
      const timeZone = this.dataset.timezone;

      if (!timeZone || timeZone === 'local') {
        return new Date(year, month - 1, day, hour, minute).getTime();
      }

      // Treat the wall-clock time as UTC, then shift by the zone's offset (handles DST).
      const wallTime = Date.UTC(year, month - 1, day, hour, minute);
      let utc = wallTime - PromoCountdown.getZoneOffset(wallTime, timeZone);
      const offsetAtUtc = PromoCountdown.getZoneOffset(utc, timeZone);
      if (wallTime - offsetAtUtc !== utc) utc = wallTime - offsetAtUtc;
      return utc;
    }

    /**
     * Gets a time zone's offset from UTC at a given moment.
     * @param {number} timestamp - Milliseconds since epoch.
     * @param {string} timeZone - IANA time zone name.
     * @returns {number} Offset in milliseconds.
     */
    static getZoneOffset(timestamp, timeZone) {
      const parts = {};
      new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric'
      })
        .formatToParts(new Date(timestamp))
        .forEach((part) => {
          parts[part.type] = Number(part.value);
        });

      const zoned = Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour % 24,
        parts.minute,
        parts.second
      );
      return zoned - Math.floor(timestamp / 1000) * 1000;
    }

    tick() {
      const remaining = Math.max(0, this.endTime - Date.now());
      const totalSeconds = Math.floor(remaining / 1000);
      const days = Math.floor(totalSeconds / 86400);

      this.units.days.textContent = String(days).padStart(2, '0');
      this.units.hours.textContent = String(Math.floor((totalSeconds % 86400) / 3600)).padStart(2, '0');
      this.units.minutes.textContent = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
      this.units.seconds.textContent = String(totalSeconds % 60).padStart(2, '0');
      this.daysUnit.hidden = days === 0;

      if (remaining === 0) {
        clearInterval(this.interval);
        this.expire();
      }
    }

    /**
     * Switches the strip back to its full (non-countdown) mobile copy.
     */
    endCountdownCopy() {
      const strip = this.closest('.promo-strip--countdown-active');
      if (strip) strip.classList.remove('promo-strip--countdown-active');
    }

    expire() {
      // Keep everything visible in the theme editor so it can still be edited.
      if (window.Shopify && Shopify.designMode) return;

      if (this.dataset.onExpire === 'hide_section') {
        const section = this.closest('.shopify-section');
        if (section) section.hidden = true;
      } else {
        this.block.hidden = true;
      }

      this.endCountdownCopy();

      const header = document.querySelector('store-header');
      if (header && typeof header.setHeaderEnd === 'function') header.setHeaderEnd();
    }
  }

  customElements.define('promo-countdown', PromoCountdown);
}
